import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import Category from '../../../models/Category';
import InfoPage from '../../../models/InfoPage';
import FullProduct from '../../../models/product/FullProduct';
import Section from '../../../models/Section';
import { modelOf } from '../../../prop-types';
import RouteService, { X_DEFAULT } from '../../../services/RouteService';
import LocationContext from '../../../services/LocationContext';
import LanguageStore from '../../../store/LanguageStore';
import SectionStore from '../../../store/SectionStore';
import SEOLink from '../SEOLink';

import ProductClass from '../../../types/ProductClass';
import ConfigStore from '../../../store/ConfigStore';

const AlternateLink = ({
  configStore,
  languageStore,
  routeService,
  locationContext,
  sectionStore,
  model,
  isProductPage,
  isInfoPage,
  path,
  pathSuffix,
}) => {
  const languages = languageStore.languages;
  if (languages.length <= 1) {
    return null;
  }

  const appendPathSuffix = (basePath, suffix) => {
    if (!suffix) {
      return basePath;
    }

    const normalizedBase = basePath.endsWith('/')
      ? basePath.slice(0, -1)
      : basePath;
    const normalizedSuffix = suffix.startsWith('/') ? suffix : `/${suffix}`;
    return `${normalizedBase}${normalizedSuffix}`;
  };

  const renderAlternateLinks = () => {
    if (path && !model) {
      return renderStaticPathAlternateLinks();
    }

    if (!model || !model.alternate_links) {
      return null;
    }

    const links = [];
    Object.entries(model.alternate_links).forEach(
      ([domain, alternateLinks]) => {
        alternateLinks.forEach(
          ({ hreflang, slug, section: alternateLinkSection }) => {
            const languageCode = hreflang;
            const activeId =
              isProductPage && model.class === ProductClass.MULTI_CHILD
                ? model.id
                : null;
            const modelPath = model.getLocalizedPath(
              hreflang,
              activeId,
              domain
            );

            let section = null;
            if (alternateLinkSection) {
              section = { slug: alternateLinkSection };
            } else if (isProductPage) {
              section = alternateLinkSection ? { slug } : alternateLinkSection;
            }

            const fullPath = appendPathSuffix(
              routeService.getPath(modelPath, section, languageCode),
              pathSuffix
            );
            const fullUrl = `${locationContext.protocol}//${domain}${fullPath}`;

            links.push(
              <SEOLink
                key={`${domain}-${hreflang}`}
                rel="alternate"
                path={fullUrl}
                hreflang={hreflang}
              />
            );
          }
        );
      }
    );
    return links;
  };

  const renderStaticPathAlternateLinks = () => {
    const links = [];

    if (
      !locationContext?.host ||
      !locationContext?.protocol ||
      !configStore?.defaultLocale
    ) {
      return [];
    }

    const currentDomain = locationContext.host;

    languages.forEach((language) => {
      const languageCode = language.code;
      const hreflang = languageCode;

      const localizedPath = appendPathSuffix(
        routeService.getPath(path, sectionStore.activeSection, languageCode),
        pathSuffix
      );
      if (!localizedPath) {
        return;
      }

      const fullUrl = `${locationContext.protocol}//${currentDomain}${localizedPath}`;

      links.push(
        <SEOLink
          key={`${currentDomain}-${hreflang}`}
          rel="alternate"
          path={fullUrl}
          hreflang={hreflang}
        />
      );
    });

    return links;
  };

  const getXDefaultPath = () => {
    if (path && !model) {
      if (
        !locationContext?.host ||
        !locationContext?.protocol ||
        !configStore?.defaultLocale
      ) {
        return null;
      }

      const defaultLanguageCode = configStore.defaultLocale;
      const defaultPath = routeService.getPath(
        path,
        sectionStore.activeSection,
        defaultLanguageCode
      );
      if (!defaultPath) {
        return null;
      }

      return `${locationContext.protocol}//${locationContext.host}${defaultPath}`;
    }

    if (model && model.alternate_links) {
      for (const domainKey of Object.keys(model.alternate_links)) {
        const defaultLink = model.alternate_links[domainKey].find(
          (link) => link.hreflang === configStore.defaultLocale
        );
        if (defaultLink) {
          const activeId =
            isProductPage && model.class === ProductClass.MULTI_CHILD
              ? model.id
              : null;
          const modelPath = model.getLocalizedPath(
            defaultLink.hreflang,
            activeId,
            domainKey
          );
          const modelPathWithSuffix = appendPathSuffix(modelPath, pathSuffix);

          let section = null;
          if (defaultLink.section) {
            section = { slug: defaultLink.section };
          } else if (isProductPage) {
            const sectionSlug = model.getSectionFromAlternateLinks?.(
              defaultLink.hreflang,
              domainKey
            );
            section = sectionSlug
              ? { slug: sectionSlug }
              : sectionStore.activeSection;
          }

          const fullPath = routeService.getPath(
            modelPathWithSuffix,
            section,
            X_DEFAULT
          );
          return `${locationContext.protocol}//${domainKey}${fullPath}`;
        }
      }
    }
    return null;
  };

  const xDefaultPath = getXDefaultPath();

  return (
    <>
      {renderAlternateLinks()}
      {xDefaultPath && (
        <SEOLink rel="alternate" path={xDefaultPath} hreflang="x-default" />
      )}
    </>
  );
};

AlternateLink.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  languageStore: modelOf(LanguageStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  locationContext: PropTypes.instanceOf(LocationContext).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  model: PropTypes.oneOfType([
    modelOf(Category),
    modelOf(FullProduct),
    modelOf(Section),
    modelOf(InfoPage),
    PropTypes.object,
  ]),
  path: PropTypes.string,
  pathSuffix: PropTypes.string,
  isProductPage: PropTypes.bool,
  isInfoPage: PropTypes.bool,
};

export default inject(
  'configStore',
  'languageStore',
  'routeService',
  'locationContext',
  'sectionStore'
)(observer(AlternateLink));
